import { rememberFactorId, suggestDeviceName, verifyTotpCode } from "@/auth/mfaFactors";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { toast } from "sonner";

interface MFAEnrollmentProps {
  onComplete: () => void;
  onCancel?: () => void;
}

type Step = "loading" | "name" | "verify" | "error";

export const MFAEnrollment = ({ onComplete, onCancel }: MFAEnrollmentProps) => {
  const [step, setStep] = useState<Step>("loading");
  const [deviceName, setDeviceName] = useState("");
  const [takenNames, setTakenNames] = useState<string[]>([]);
  const [qrCode, setQrCode] = useState("");
  const [secret, setSecret] = useState("");
  const [factorId, setFactorId] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const prepare = async () => {
      try {
        const { data, error: listError } = await supabase.auth.mfa.listFactors();
        if (listError) throw listError;

        const all = data?.all ?? [];
        const isVerified = (factor: { status: string }) => factor.status === "verified";

        const stale = all.filter((factor) => factor.factor_type === "totp" && !isVerified(factor));
        const cleanups = await Promise.allSettled(
          stale.map((factor) => supabase.auth.mfa.unenroll({ factorId: factor.id })),
        );
        const leftovers = stale.filter((_, index) => {
          const cleanup = cleanups[index];
          return cleanup.status === "rejected" || cleanup.value.error;
        });
        const names = [...all.filter(isVerified), ...leftovers].map((factor) => factor.friendly_name ?? "");

        if (!isMounted) return;
        setTakenNames(names);
        setDeviceName(suggestDeviceName(names));
        setStep("name");
      } catch (err: unknown) {
        if (!isMounted) return;
        setError("Failed to start MFA enrollment");
        setStep("error");
        toast.error(err instanceof Error ? err.message : "Failed to start MFA enrollment");
      }
    };

    prepare();

    return () => {
      isMounted = false;
    };
  }, []);

  const fail = (message: string) => {
    setError(message);
    toast.error(message);
  };

  const isNameTaken = takenNames.some((name) => name.toLowerCase() === deviceName.trim().toLowerCase());

  const startEnrollment = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: enrollError } = await supabase.auth.mfa.enroll({
        factorType: "totp",
        friendlyName: deviceName.trim(),
      });
      if (enrollError) {
        fail(enrollError.message);
        return;
      }

      setQrCode(data.totp.qr_code);
      setSecret(data.totp.secret);
      setFactorId(data.id);
      setStep("verify");
    } catch (err: unknown) {
      fail(err instanceof Error ? err.message : "Failed to start MFA enrollment");
    } finally {
      setLoading(false);
    }
  };

  const verifyEnrollment = async () => {
    if (verificationCode.length !== 6) {
      toast.error("Please enter a 6-digit verification code");
      return;
    }

    setLoading(true);
    setError(null);

    const { error: verifyError } = await verifyTotpCode({ factorId, code: verificationCode });
    setLoading(false);

    if (verifyError) {
      setError("Invalid verification code. Please try again.");
      toast.error(verifyError);
      return;
    }

    rememberFactorId(factorId);
    toast.success("MFA device enrolled successfully!");
    onComplete();
  };

  const cancel = async () => {
    if (step === "verify" && factorId) {
      setLoading(true);
      try {
        const { error: unenrollError } = await supabase.auth.mfa.unenroll({ factorId });
        if (unenrollError) throw unenrollError;
      } catch {
        fail("Could not remove the pending device. Please try again.");
        return;
      } finally {
        setLoading(false);
      }
    }

    onCancel?.();
  };

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      toast.success("Secret key copied to clipboard");
    } catch {
      toast.error("Could not copy secret to clipboard");
    }
  };

  const cancelButton = onCancel && (
    <Button variant="outline" onClick={cancel} disabled={loading}>
      Cancel
    </Button>
  );

  if (step === "loading") {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Checking your devices</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">Loading...</p>
        </CardContent>
      </Card>
    );
  }

  if (step === "error") {
    return (
      <Card>
        <CardHeader>
          <CardTitle>MFA Enrollment Error</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-red-500 text-sm mb-4">{error}</div>
          {onCancel && (
            <Button variant="outline" onClick={onCancel}>
              Back
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  if (step === "name") {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Add an authenticator device</CardTitle>
          <CardDescription>Name the device so you can tell your devices apart when you sign in</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <AlertDescription>
              You'll need an authenticator app such as Google Authenticator, Authy or 1Password on this device.
            </AlertDescription>
          </Alert>
          <div className="space-y-2">
            <Label htmlFor="device-name">Device name</Label>
            <Input
              id="device-name"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              maxLength={60}
              disabled={loading}
            />
            {isNameTaken && <div className="text-red-500 text-sm mt-1">That name is already in use</div>}
            {error && <div className="text-red-500 text-sm mt-1">{error}</div>}
          </div>
          <div className="flex gap-2">
            <Button onClick={startEnrollment} disabled={loading || isNameTaken || deviceName.trim() === ""}>
              {loading ? "Generating..." : "Generate QR Code"}
            </Button>
            {cancelButton}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Scan QR Code</CardTitle>
        <CardDescription>
          Scan this QR code with the authenticator app on "{deviceName.trim()}", then enter the 6-digit code
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 sm:space-y-6">
        <Alert>
          <AlertDescription>Adding a device signs you out of your other browsers.</AlertDescription>
        </Alert>
        {qrCode && (
          <div className="flex flex-col items-center space-y-3 sm:space-y-4">
            <img alt="MFA QR Code" className="bg-white p-4 rounded-lg max-w-[240px] w-full h-auto" src={qrCode} />
            <div className="text-center w-full">
              <Label className="text-sm font-medium">Manual Entry Key</Label>
              <div className="flex items-center gap-2 mt-1 flex-wrap justify-center">
                <code className="text-xs bg-muted px-2 py-1 rounded break-all max-w-[200px] sm:max-w-none">
                  {secret}
                </code>
                <Button size="sm" variant="outline" onClick={copySecret} className="shrink-0">
                  Copy
                </Button>
              </div>
            </div>
          </div>
        )}
        <div className="space-y-2">
          <Label htmlFor="verification-code">Verification Code</Label>
          <Input
            id="verification-code"
            type="text"
            placeholder="Enter 6-digit code"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            maxLength={6}
            autoComplete="one-time-code"
          />
          {error && <div className="text-red-500 text-sm mt-1">{error}</div>}
        </div>
        <div className="flex gap-2">
          <Button onClick={verifyEnrollment} disabled={loading || verificationCode.length !== 6}>
            {loading ? "Verifying..." : "Verify & Complete"}
          </Button>
          {cancelButton}
        </div>
      </CardContent>
    </Card>
  );
};
